import { AND } from '~/common/constants/openSearch';
import { PilotId } from '~/eds/src/types/PilotId';
import { Filter } from '~/evifields/src/types/Filter';
import { SearchFilter } from '~/types/DashboardV2';
import {
  BooleanQueryEntity,
  FilterQueryEntity,
  QueryOperatorEntity,
  SectionEntity,
} from '~/types/OpenSearch';

import { BuildQueryParams, SearchFilterRecord, SearchQuery } from '../types';
import { toEntity } from './filters';
import { testIsFilter, testIsSection } from './query';

// Static filter IDs from the legacy Pilot query format (old saved searches, Ask AI
// scope handoff) that predate evisearch's <name>_Document filter ID convention.
const LEGACY_PILOT_FILTER_IDS: Record<string, string> = {
  clause: 'clause_Document',
  folder: 'folder_Document',
  filename: 'name_Document',
  content: 'document_content_Document',
};

export const testIsFilterEmpty = (filter: Filter) => !filter.operatorId;

export const buildComplexQueryWithCrossFilters = (
  queryBuilderData: SearchQuery,
  initialCrossFilters: Filter[],
  searchFilters: Record<string, SearchFilter>,
  booleanQuery?: string,
): SearchQuery => {
  let query: SearchQuery = booleanQuery
    ? [{ type: 'bool_text_search', value: booleanQuery }]
    : [];

  let crossFilters = initialCrossFilters;
  if (searchFilters) {
    crossFilters = attachFilterType(crossFilters, searchFilters);
  }

  if (queryBuilderData.length || crossFilters.length) {
    if (booleanQuery) {
      query.push(AND);
    }
    if (queryBuilderData.length) {
      query = query.concat(queryBuilderData);
      query.push(AND);
    }

    crossFilters.forEach((crossFilter) => {
      if (crossFilter.fieldType) {
        query.push(toEntity(crossFilter, crossFilter.fieldType));
        query.push(AND);
      }
    });

    query.pop();
  }

  return query;
};

export const buildQuery = (payload?: BuildQueryParams): SearchQuery => {
  if (!payload) {
    return [];
  }

  const {
    booleanQuery,
    crossFilters = [],
    filters = [],
    searchFilters = {},
  } = payload;

  const query:
    | SectionEntity[]
    | Array<
        FilterQueryEntity<any, any> | BooleanQueryEntity | QueryOperatorEntity
      > = booleanQuery
    ? [{ type: 'bool_text_search', value: booleanQuery }]
    : [];

  if (filters.length || crossFilters.length) {
    if (booleanQuery) {
      query.push(AND);
    }

    filters.forEach((filter) => {
      const resolvedFilter = resolveSearchFilter(filter, searchFilters);
      if (resolvedFilter) {
        query.push(toEntity(resolvedFilter.filter, resolvedFilter.fieldType));
        query.push(AND);
      }
    });

    crossFilters.forEach((crossFilter) => {
      const resolvedFilter = resolveSearchFilter(crossFilter, searchFilters);
      if (resolvedFilter) {
        query.push(toEntity(resolvedFilter.filter, resolvedFilter.fieldType));
        query.push(AND);
      }
    });

    query.pop();
  }

  return query;
};

// TODO: Will potentially replace buildQuery once we get it working with query builder and cross filter as well.
export const buildFilterQuery = (payload?: BuildQueryParams): any => {
  if (!payload) {
    return [];
  }

  const {
    booleanQuery,
    crossFilters: initialCrossFilters = [],
    filters: initialFilters = [],
    searchFilters,
  } = payload;

  const query:
    | SectionEntity[]
    | Array<
        FilterQueryEntity<any, any> | BooleanQueryEntity | QueryOperatorEntity
      > = booleanQuery
    ? [{ type: 'bool_text_search', value: booleanQuery }]
    : [];

  let filters = initialFilters;
  let crossFilters = initialCrossFilters;
  if (searchFilters) {
    filters = attachFilterType(filters, searchFilters);
    crossFilters = attachFilterType(crossFilters, searchFilters);
  }

  if (filters.length || crossFilters.length) {
    if (booleanQuery) {
      query.push(AND);
    }

    filters.forEach((filter) => {
      if (filter.fieldType) {
        query.push(toEntity(filter, filter.fieldType));
        query.push(AND);
      }
    });

    crossFilters.forEach((crossFilter) => {
      if (crossFilter.fieldType) {
        query.push(toEntity(crossFilter, crossFilter.fieldType));
        query.push(AND);
      }
    });

    query.pop();
  }

  return query;
};

const attachFilterType = (
  filters: Filter[],
  searchFilters: SearchFilterRecord = {},
) =>
  filters.map((filter) => {
    const resolvedFilter = resolveSearchFilter(filter, searchFilters);
    const updatedFilter = { ...filter };
    if (resolvedFilter) {
      updatedFilter.fieldId = resolvedFilter.filter.fieldId;
      updatedFilter.operatorId = resolvedFilter.filter.operatorId;
      updatedFilter.fieldType = resolvedFilter.fieldType;
    }
    return updatedFilter;
  });

const resolveSearchFilter = (
  filter: Filter,
  searchFilters: SearchFilterRecord = {},
) => {
  const searchFilter = searchFilters[filter.fieldId];
  if (searchFilter) {
    return { filter, searchFilter, fieldType: searchFilter.type };
  }

  const evisearchId = LEGACY_PILOT_FILTER_IDS[filter.fieldId as string];
  const legacySearchFilter = evisearchId
    ? searchFilters[evisearchId]
    : undefined;
  if (!legacySearchFilter) return undefined;

  // Clause filters use a distinct value shape (provision + optional text search),
  // not the plain enum_set value_list evisearch reports for clause_Document.
  const fieldType =
    filter.fieldId === 'clause' ? ('clause' as const) : legacySearchFilter.type;

  return {
    filter: {
      ...filter,
      fieldId: legacySearchFilter.id,
      operatorId: toEvisearchOperator(filter.fieldId, filter.operatorId),
    },
    searchFilter: legacySearchFilter,
    fieldType,
  };
};

// Folder is the only legacy filter needing an operator override, since Evisearch
// expects `contains_any` while the ID is still the untranslated 'folder'.
const toEvisearchOperator = <T>(fieldId: PilotId, operatorId: T) =>
  fieldId === 'folder' ? ('contains_any' as const) : operatorId;

export const resolveLegacyFilterIds = (
  query: SearchQuery,
  searchFilters: SearchFilterRecord = {},
): SearchQuery =>
  query.map((entity) => {
    if (testIsSection(entity)) {
      return {
        ...entity,
        value: resolveLegacyFilterIds(
          entity.value as SearchQuery,
          searchFilters,
        ),
      };
    }
    if (!testIsFilter(entity) || searchFilters[entity.id]) {
      return entity;
    }

    const evisearchId = LEGACY_PILOT_FILTER_IDS[String(entity.id)];
    if (!evisearchId || !searchFilters[evisearchId]) {
      return entity;
    }

    return {
      ...entity,
      id: evisearchId,
      operator: toEvisearchOperator(entity.id, entity.operator),
    };
  });
